import React, {
    Component,
    Fragment,
    useState,
    useEffect,
    useContext,
    useRef,
    useCallback,
    useMemo,
  } from "react";
  import { useTranslation } from "react-i18next";
  
  // d.ts
  import {
    DrawingType,
    CoordinateSystemType,
    BoolSettings,
    ViewOptionsType,
    IMapView,
  } from "@/@types/common/index";
  import {
    IMapProps,
    IMapState,
    CardPosition,
    CardOverlayType,
    ICardOverlay,
    ICardOverlayData,
  } from "@/@types/views/GIS/map";
  
  // OpenLayers
  import Geometry from "ol/geom/Geometry";
  import proj4, { Point } from "proj4";
  import { register as OlRegister } from "ol/proj/proj4";
  import { get as OlGetProjection } from "ol/proj";
  import Projection from "ol/proj/Projection";
  import OlLayerGroup from "ol/layer/Group";
  import OlSourceVector from "ol/source/Vector";
  import Map from "@/components/Map/Map";
  import MapEvent from "ol/MapEvent";
  import { DrawEvent } from "ol/interaction/Draw";
  import BaseEvent from "ol/events/Event";
  import OlMapBrowserEvent from "ol/MapBrowserEvent";
  import OlBaseLayer from "ol/layer/Base";
  import OlFeature from "ol/Feature";
  import OlCollection from "ol/Collection";
  import OlPoint from "ol/geom/Point";
  import OlLineString from "ol/geom/LineString";
  import OlMultiLineString from "ol/geom/MultiLineString";
  import OlFormatWKT from "ol/format/WKT";
  
  
  import modelComments from "@/models/komentari";
  import modelCommentPhotos from "@/models/komentari_fotografije";
  import CommentInfoCard from "@/views/Komentari/CommentInfoCard";
  import CommentDialog from "@/views/Komentari/CommentDialog";
  import { Permissions } from "@/lib/permissions";
  
  
  // Custom
  import {
    Controls,
    AttributionControl,
    CoordZoomStatusControl,
    DefaultControls,
    FullScreenControl,
    OverviewMapControl,
    RotateControl,
    ScaleLineControl,
    ScaleRatioControl,
    StatusControl,
    ZoomControl,
    ZoomToExtentControl,
    GeoLocateControl,
    RadiusRulerControl,
    ZoomSliderControl,
    CenterMarkerControl,
    LayerTree,
    ViewHistoryControl,
  } from "@/components/Map/Controls";
  
  import {
    Layers
  } from "@/components/Map/Layers";

  import {
    Interactions,
    DefaultInteractions,
    DrawInteraction,
  } from "@/components/Map/Interactions";
  import UserContext from "@/components/UserContext/UserContext";
  import DialogContext from "@/ui/DialogContext/DialogContext";
  import { DCRecord } from "@/@types/lib/dataController";

  
  import GeoBaseLayerSwitcher from "@/components/Map/Controls/GEO/GeoBaseLayerSwticher";
  import GeoAPILayers from "@/components/Map/Layers/GEO/GeoAPILayers";

  
  //Services
  import mapService from "@/services/mapService";
  import gsService from "@/services/gsService";
  import RenderFeature from "ol/render/Feature";
  import LayerTreeItem from "@/components/Map/Controls/Custom/LayerTree/LayerTreeItem";
  import { CheckBox } from "@mui/icons-material";
  import { Checkbox, FormControlLabel } from "@mui/material";
  import api from "@/lib/api";
  import DateRangeControl from "@/components/Map/Controls/Custom/DateRangeControl";
  
  function ReportsMap() {
    const userContext = useContext(UserContext);
    const dialogContext = useContext(DialogContext);
    const { t } = useTranslation();
  
    const mapId = 8
  
    const [mapInitialized, setMapInitialized] = useState(false);
  
    const [recordsZemljovidi, setRecordsZemljovidi] = useState<DCRecord[]>([]);
  
    // localStorage map state
    const mapStateJSON = localStorage.getItem(
      'map_state_' + userContext?.firstName.toLowerCase()
    );
    const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
      center: 
        mapStateJSON && mapStateJSON !== null &&
        mapStateJSON !== undefined && JSON.parse(mapStateJSON).center &&
        JSON.parse(mapStateJSON).center !== null
          ? JSON.parse(mapStateJSON).center
          : [1731757, 5581737],
      zoom: 
        mapStateJSON && mapStateJSON !== null &&
        mapStateJSON !== undefined && JSON.parse(mapStateJSON).zoom &&
        JSON.parse(mapStateJSON).zoom !== null
          ? JSON.parse(mapStateJSON).zoom
          : 8,
      //extent: userContext?.mapSettings ? userContext.mapSettings.max_extent : undefined,
      extent: [1447153, 5104226, 2162937, 5867310],
      constrainOnlyCenter: true,
      //projection: wgs84PM,
      minZoom: 8,
      maxZoom: 18,
      constrainResolution: true,
    });
  
    const [layersCollection, setLayersCollection] = useState<
      OlCollection<OlBaseLayer> | undefined
    >(undefined);

    const [defaultExtent, setDefaultExtent] = useState(
      userContext?.mapSettings?.default_extent
        ? userContext.mapSettings.default_extent
        : [1437016, 5271097, 2206278, 5860579]
    );
    const [zoomToExtent, setZoomToExtent] = useState(undefined);
   
    const fullScreenElementRef = useRef<HTMLLIElement>(null);
    const dataRangeControl = useRef<HTMLLIElement>(null);
    //define proj
    proj4.defs(
      "EPSG:3765",
      "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
    );
    proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
    proj4.defs(
      "EPSG:31276",
      "+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs"
    );
    proj4.defs(
      "EPSG:3857",
      "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
    );
    OlRegister(proj4);
  
    const htrs96 = OlGetProjection("EPSG:3765");
    const wgs84 = OlGetProjection("EPSG:4326");
    const wgs84PM = OlGetProjection("EPSG:3857");
  
    // defaultViewCenter = [1731757, 5581737];
    // initialDefaultExtent = [1688674,5501166,1688774,5501266];
    const defaultViewCenter = userContext?.mapSettings?.initial_view_center;
    const initialDefaultExtent = userContext?.mapSettings?.default_extent;
 
    useEffect(() => {
      getLayers();
    }, []);
  
    const getLayers = () => {
      mapService.getLayers(mapId).then((coll) => {
        setLayersCollection(coll);
        setMapInitialized(true);
      });
    };
  
     return (
      <Fragment>
        {userContext && layersCollection ? (
          <Map
            mapId={mapId}
            height="800px"
            view={viewOptions}
            className="sidebar-map"
            id="reports-map"
            initialized={mapInitialized}
            moveTolerance={10}
            maxTilesLoading={10}
            zoomToExtent={zoomToExtent}
          >
            <Controls>
             <ZoomControl
                zoomInTipLabel={t("map:controls.zoom_in")}
                zoomOutTipLabel={t("map:controls.zoom_out")}
              />
             
              {/* <ScaleRatioControl ppi={96} /> */}
              {/* <ScaleLineControl /> */}
              {/* <FullScreenControl id="full-screen-ctrl" tipLabel={t("map:controls.full_screen")} /> */}
              <RotateControl
                autoHide={false}
                tipLabel={t("map:controls.reset_north")}
              />
              <ViewHistoryControl />
              {fullScreenElementRef?.current && mapInitialized ? (
                <FullScreenControl
                  id="full-screen"
                  tipLabel={t("map:controls.full_screen")}
                  className="ol-sidebar-control"
                />
              ) : null}

              <DateRangeControl/>
  
           
              <GeoBaseLayerSwitcher mapId={mapId} allowNoLayer={false} />
            </Controls>
            <Layers>
           
              {layersCollection ? (
                  <GeoAPILayers layersCollection={layersCollection} forceVisible={true}/>
              ) : null}
            </Layers>
            <Interactions>
              <DefaultInteractions />
            </Interactions>
          </Map>
        ) : null}
      </Fragment>
    );
  }
  
  export default ReportsMap;
  